import { Alert, Button, Flex, Image, Spin, Typography } from 'antd';
import { useTranslation } from 'translations/hooks';

import { useGetRingPictures } from 'business/quality-control/pages/ring-compliance-report-detail/hooks/use-get-pictures';
import { DefectPictureGroupDto, PictureOutputDto } from 'generated/apiSchemas';

import styles from './index.module.scss';

const { Title, Text } = Typography;

const PictureGrid = ({ pictures }: { pictures: PictureOutputDto[] }) => {
  if (pictures.length === 0) {
    return null;
  }

  return (
    <div className={styles.photos}>
      {pictures.map((picture) => (
        <Image key={picture.id} src={picture.path} className={styles.photo} />
      ))}
    </div>
  );
};

const hasDefectPictures = (defect: DefectPictureGroupDto) =>
  defect.declarationPictures.length > 0 ||
  (defect.repair?.pictures.length ?? 0) > 0;

const LabeledPictures = ({
  label,
  pictures,
}: {
  label: string;
  pictures: PictureOutputDto[];
}) => {
  if (pictures.length === 0) {
    return null;
  }

  return (
    <>
      <Flex vertical align="center">
        <Text type="secondary">{label}</Text>
      </Flex>
      <PictureGrid pictures={pictures} />
    </>
  );
};

const DefectPictureSection = ({
  defect,
}: {
  defect: DefectPictureGroupDto;
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.defect}>
      <LabeledPictures
        label={t(
          'qualityControl.ringComplianceReport.detail.annex.declarationLabel',
          { label: defect.defectTypeLabel },
        )}
        pictures={defect.declarationPictures}
      />
      <LabeledPictures
        label={t(
          'qualityControl.ringComplianceReport.detail.annex.repairLabel',
          { label: defect.defectTypeLabel },
        )}
        pictures={defect.repair?.pictures ?? []}
      />
    </div>
  );
};

// Record (not a plain array) so adding a member to DefectPictureGroupDto['defectType']
// forces a compile error here until its display order is decided.
const DEFECT_TYPE_ORDER_WEIGHT: Record<
  DefectPictureGroupDto['defectType'],
  number
> = {
  quarantine: 0,
  tunnel: 1,
};

const DEFECT_TYPES = (
  Object.keys(DEFECT_TYPE_ORDER_WEIGHT) as DefectPictureGroupDto['defectType'][]
).sort((a, b) => DEFECT_TYPE_ORDER_WEIGHT[a] - DEFECT_TYPE_ORDER_WEIGHT[b]);

const DefectTypeGroup = ({
  defectType,
  defects,
}: {
  defectType: DefectPictureGroupDto['defectType'];
  defects: DefectPictureGroupDto[];
}) => {
  const { t } = useTranslation();

  const visibleDefects = defects.filter(hasDefectPictures);
  if (visibleDefects.length === 0) {
    return null;
  }

  return (
    <div className={styles.defectTypeGroup}>
      <Text strong>
        {t(
          `qualityControl.ringComplianceReport.detail.annex.defectType.${defectType}`,
        )}
      </Text>
      {visibleDefects.map((defect) => (
        <DefectPictureSection key={defect.defectId} defect={defect} />
      ))}
    </div>
  );
};

export const RingPicturesAnnex = ({ ringId }: { ringId: string }) => {
  const { t } = useTranslation();
  const { items, isLoading, isError, retry } = useGetRingPictures(ringId);

  if (isLoading) {
    return (
      <Flex justify="center" style={{ padding: '24px 0' }}>
        <Spin tip={t('common.loading')}>
          <div style={{ minHeight: 60, minWidth: 60 }} />
        </Spin>
      </Flex>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        message={t(
          'qualityControl.ringComplianceReport.detail.annex.loadError',
        )}
        action={
          <Button size="small" onClick={retry}>
            {t('qualityControl.ringComplianceReport.detail.annex.retry')}
          </Button>
        }
      />
    );
  }

  if (items.length === 0) {
    return null;
  }

  const defectsBySegmentType = new Map<string, DefectPictureGroupDto[]>();
  for (const item of items) {
    const group = defectsBySegmentType.get(item.segmentType) ?? [];
    group.push(item);
    defectsBySegmentType.set(item.segmentType, group);
  }

  const sortedSegments = Array.from(defectsBySegmentType.entries()).sort(
    ([a], [b]) => a.localeCompare(b),
  );

  return (
    <Flex vertical gap={12} className={styles.annex}>
      <Title level={4}>
        {t('qualityControl.ringComplianceReport.detail.annex.title')}
      </Title>
      {sortedSegments.map(([segmentType, defects]) => {
        const defectsByType = new Map<
          DefectPictureGroupDto['defectType'],
          DefectPictureGroupDto[]
        >();
        for (const defect of defects) {
          const group = defectsByType.get(defect.defectType) ?? [];
          group.push(defect);
          defectsByType.set(defect.defectType, group);
        }

        return (
          <div key={segmentType} className={styles.segment}>
            <Text strong className={styles.segmentLabel}>
              {t('qualityControl.ringComplianceReport.detail.annex.segment', {
                label: segmentType,
              })}
            </Text>
            {DEFECT_TYPES.map((defectType) => (
              <DefectTypeGroup
                key={defectType}
                defectType={defectType}
                defects={defectsByType.get(defectType) ?? []}
              />
            ))}
          </div>
        );
      })}
    </Flex>
  );
};
